import React, { useEffect, useState, useRef } from 'react';
import { getBackendUrl } from '@/config';
import { useAuth } from '@/contexts/AuthContext';
import { taxFilingAPI } from '@/services/api';
import { toast } from 'sonner';
import { useNavigate } from 'react-router-dom';
import { DashboardLayout } from '@/components/layout/DashboardLayout';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { ScrollArea } from '@/components/ui/scroll-area';
import { CheckCircle2, Clock, AlertCircle, FileCheck, ArrowRight, Upload, Send, Download, FileText, MessageSquare, Eye, File } from 'lucide-react';
import { cn } from '@/lib/utils';
import axios from 'axios';
import { getDocuments } from '@/services/uploadService';

// Status configuration
const STATUS_CONFIG = {
  SUBMITTED: {
    label: 'Submitted',
    color: 'success',
    badgeClass: 'bg-green-500',
    borderClass: 'border-l-green-500',
    icon: CheckCircle2,
  },
  NEED_INFO: {
    label: 'Waiting for Info',
    color: 'warning',
    badgeClass: 'bg-yellow-500',
    borderClass: 'border-l-yellow-500',
    icon: AlertCircle,
  },
  READY_FOR_REVIEW: {
    label: 'Ready for Review',
    color: 'success',
    badgeClass: 'bg-green-500',
    borderClass: 'border-l-green-500',
    icon: FileCheck,
  },
  SUBMITTED_TO_IRS: {
    label: 'Submitted to IRS',
    color: 'success',
    badgeClass: 'bg-green-600',
    borderClass: 'border-l-green-600',
    icon: Send,
  },
  DRAFT: {
    label: 'Draft',
    color: 'muted',
    badgeClass: 'bg-gray-500',
    borderClass: 'border-l-gray-500',
    icon: Clock,
  },
};

// Timeline steps order
const TIMELINE_STEPS = ['SUBMITTED', 'NEED_INFO', 'READY_FOR_REVIEW', 'SUBMITTED_TO_IRS'];

export default function FilingStatusPage() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [filings, setFilings] = useState([]);
  const [loading, setLoading] = useState(true);
  const [currentFiling, setCurrentFiling] = useState(null);
  const [uploadedDocuments, setUploadedDocuments] = useState([]);
  const [messages, setMessages] = useState([]);
  const [newMessage, setNewMessage] = useState('');
  const [sendingMessage, setSendingMessage] = useState(false);
  const messagesEndRef = useRef(null);

  // Fetch uploaded documents for current filing
  const fetchUploadedDocuments = async (filingId) => {
    try {
      const docs = await getDocuments('tax_filing', filingId);
      setUploadedDocuments(docs);
    } catch (error) {
      console.error('Error fetching documents:', error);
    }
  };

  // View document - clients can view via proxy
  const handleViewUploadedDoc = async (documentId, url) => {
    const backendUrl = getBackendUrl();
    const token = localStorage.getItem('access_token');
    
    try {
      const viewUrl = documentId 
        ? `${backendUrl}/api/files/view/${documentId}`
        : (url.startsWith('http') ? url : `${backendUrl}${url}`);
      
      const response = await fetch(viewUrl, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });
      
      if (!response.ok) {
        throw new Error('Failed to load document');
      }
      
      const blob = await response.blob();
      const blobUrl = URL.createObjectURL(blob);
      window.open(blobUrl, '_blank');
    } catch (error) {
      console.error('Error viewing document:', error);
    }
  };

  // Auto-scroll to latest message within container
  const scrollToBottom = () => {
    const scrollArea = messagesEndRef.current?.closest('[data-radix-scroll-area-viewport]');
    if (scrollArea) {
      scrollArea.scrollTop = scrollArea.scrollHeight;
    }
  };

  useEffect(() => {
    if (messages.length > 0) {
      setTimeout(scrollToBottom, 100);
    }
  }, [messages]);

  // Fetch messages for current filing
  const fetchMessages = async (filingId) => {
    try {
      const backendUrl = getBackendUrl();
      const token = localStorage.getItem('access_token');
      const response = await axios.get(
        `${backendUrl}/api/filings/${filingId}/messages`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setMessages(response.data);
    } catch (error) {
      console.error('Error fetching messages:', error);
    }
  };

  // Send a message
  const handleSendMessage = async () => {
    if (!newMessage.trim() || !currentFiling) return;

    try {
      setSendingMessage(true);
      const backendUrl = getBackendUrl();
      const token = localStorage.getItem('access_token');
      
      const response = await axios.post(
        `${backendUrl}/api/filings/${currentFiling.id}/messages`,
        { message: newMessage.trim() },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      setMessages([...messages, response.data]);
      setNewMessage('');
    } catch (error) {
      console.error('Error sending message:', error);
      toast.error('Failed to send message');
    } finally {
      setSendingMessage(false);
    }
  };

  // Poll for new messages every 10 seconds when filing is not DRAFT
  useEffect(() => {
    if (currentFiling && currentFiling.status !== 'DRAFT') {
      fetchMessages(currentFiling.id);
      
      const interval = setInterval(() => {
        fetchMessages(currentFiling.id);
      }, 10000);
      
      return () => clearInterval(interval);
    }
  }, [currentFiling]);

  useEffect(() => {
    const fetchFilings = async () => {
      try {
        setLoading(true);
        const data = await taxFilingAPI.getFilings();
        
        // Filter for ONLY individual tax filings (not business filings)
        const individualFilings = data.filter(f => f.filing_type === 'individual');
        setFilings(individualFilings);
        
        // Get the most recent non-draft individual filing
        if (individualFilings && individualFilings.length > 0) {
          const nonDraftFilings = individualFilings.filter(f => f.status !== 'DRAFT');
          if (nonDraftFilings.length > 0) {
            setCurrentFiling(nonDraftFilings[0]);
          } else {
            // If no submitted filings, show the most recent draft
            setCurrentFiling(individualFilings[0]);
          }
        }
      } catch (error) {
        console.error('Error fetching filings:', error);
        toast.error('Failed to load filings');
      } finally {
        setLoading(false);
      }
    };

    if (user) {
      fetchFilings();
    }
  }, [user]);

  // Format date for display
  const formatDate = (dateString) => {
    if (!dateString) return null;
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  // Fetch documents when currentFiling changes
  useEffect(() => {
    if (currentFiling?.id) {
      fetchUploadedDocuments(currentFiling.id);
    }
  }, [currentFiling?.id]);

  // Get step status based on current filing status
  const getStepStatus = (stepKey, currentStatus) => {
    const currentIndex = TIMELINE_STEPS.indexOf(currentStatus);
    const stepIndex = TIMELINE_STEPS.indexOf(stepKey);
    
    // Special case: SUBMITTED step is always completed once filing is submitted
    if (stepKey === 'SUBMITTED' && currentIndex >= 0) {
      return 'completed';
    }
    
    if (stepIndex < currentIndex) {
      return 'completed';
    } else if (stepIndex === currentIndex) {
      return 'current';
    } else {
      return 'pending';
    }
  };

  // Get timestamp for a step
  const getStepTimestamp = (stepKey, filing) => {
    if (!filing) return null;
    
    switch (stepKey) {
      case 'SUBMITTED':
        return filing.submitted_at;
      case 'NEED_INFO':
        return filing.need_info_at;
      case 'READY_FOR_REVIEW':
        return filing.ready_for_review_at;
      case 'SUBMITTED_TO_IRS':
        return filing.submitted_to_irs_at;
      default:
        return null;
    }
  };

  // Build timeline steps from current filing
  const buildTimelineSteps = () => {
    if (!currentFiling) return [];
    
    const currentStatus = currentFiling.status;
    
    return TIMELINE_STEPS.map((stepKey) => {
      const config = STATUS_CONFIG[stepKey];
      const status = getStepStatus(stepKey, currentStatus);
      const timestamp = getStepTimestamp(stepKey, currentFiling);
      
      return {
        key: stepKey,
        label: config.label,
        status,
        timestamp,
        icon: config.icon,
        // Show agent message only for NEED_INFO step when it's current
        showMessage: stepKey === 'NEED_INFO' && status === 'current',
        message: currentFiling.status_message,
      };
    });
  };

  // Download agent document
  const handleDownloadDocument = async (doc, isAgentDocument = false) => {
    try {
      const backendUrl = getBackendUrl();
      const token = localStorage.getItem('access_token');
      
      // Use appropriate endpoint based on document type
      let downloadUrl;
      if (isAgentDocument && currentFiling?.id && doc.id) {
        // Agent documents stored in JSONB - use special endpoint
        downloadUrl = `${backendUrl}/api/files/agent-document/${currentFiling.id}/${doc.id}/download`;
      } else if (doc.id) {
        // Regular documents in documents table
        downloadUrl = `${backendUrl}/api/files/download/${doc.id}`;
      } else {
        // Fallback to direct path
        downloadUrl = `${backendUrl}${doc.path || doc.file_url}`;
      }

      const response = await fetch(downloadUrl, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });
      
      if (!response.ok) {
        throw new Error('Failed to download document');
      }
      
      const blob = await response.blob();
      const blobUrl = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = blobUrl;
      a.download = doc.name || doc.file_name;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(blobUrl);
    } catch (error) {
      console.error('Error downloading document:', error);
      toast.error('Failed to download document');
    }
  };

  const timelineSteps = buildTimelineSteps();
  const currentStatusConfig = currentFiling ? STATUS_CONFIG[currentFiling.status] || STATUS_CONFIG.DRAFT : null;

  if (!user) {
    return <div>Loading...</div>;
  }

  if (loading) {
    return (
      <DashboardLayout role={user.role} title="Individual Tax Filing Status" user={user}>
        <div className="flex items-center justify-center h-64">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary"></div>
        </div>
      </DashboardLayout>
    );
  }

  // No filings yet
  if (!currentFiling) {
    return (
      <DashboardLayout role={user.role} title="Individual Tax Filing Status" user={user}>
        <div className="space-y-6">
          <div>
            <h2 className="text-2xl font-bold text-foreground">Individual Tax Filing Status</h2>
            <p className="mt-1 text-muted-foreground">Track your individual tax filing progress</p>
          </div>
          
          <Card className="card-elevated">
            <CardContent className="pt-6">
              <div className="text-center py-8">
                <Clock className="h-16 w-16 mx-auto text-muted-foreground mb-4" />
                <h3 className="text-xl font-semibold mb-2">No Individual Tax Filings Yet</h3>
                <p className="text-muted-foreground mb-4">
                  You haven't submitted any individual tax filings yet. Start a new filing to track its progress here.
                </p>
                <Button onClick={() => navigate('/filing/start')} className="gap-2">
                  Start Individual Tax Filing
                  <ArrowRight className="h-4 w-4" />
                </Button>
              </div>
            </CardContent>
          </Card>
        </div>
      </DashboardLayout>
    );
  }

  return (
    <DashboardLayout role={user.role} title="Individual Tax Filing Status" user={user}>
      <div className="space-y-6">
        <div>
          <h2 className="text-2xl font-bold text-foreground">Individual Tax Filing Status</h2>
          <p className="mt-1 text-muted-foreground">
            Track your individual tax filing progress for Tax Year {currentFiling.tax_year}
          </p>
        </div>

        {/* Current Status Card */}
        <Card className={cn("card-elevated border-l-4", currentStatusConfig?.borderClass)}>
          <CardContent className="pt-6">
            <div className="flex items-start justify-between">
              <div className="flex-1">
                <p className="text-sm text-muted-foreground">Current Status</p>
                <p className="mt-1 text-2xl font-bold text-foreground">
                  {currentStatusConfig?.label}
                </p>
                
                {/* Agent Documents - Show all documents from agent */}
                {currentFiling.status !== 'DRAFT' && currentFiling.agent_documents && currentFiling.agent_documents.length > 0 && (
                  <div className="mt-4 space-y-3">
                    <p className="text-sm font-medium text-green-700 dark:text-green-300">
                      Documents from your Tax Agent ({currentFiling.agent_documents.length}):
                    </p>
                    <div className="space-y-2">
                      {currentFiling.agent_documents.map((doc, index) => (
                        <div
                          key={doc.id || index}
                          className="flex items-center justify-between p-3 bg-green-50 dark:bg-green-900/20 rounded-lg border border-green-200 dark:border-green-800"
                        >
                          <div className="flex items-center gap-3">
                            <FileText className="h-5 w-5 text-green-600 dark:text-green-400" />
                            <div>
                              <p className="text-sm font-medium">{doc.name}</p>
                              <p className="text-xs text-muted-foreground">
                                {doc.uploaded_at
                                  ? new Date(doc.uploaded_at).toLocaleDateString('en-US', {
                                      month: 'short',
                                      day: 'numeric',
                                      year: 'numeric',
                                    })
                                  : ''}
                              </p>
                            </div>
                          </div>
                          <Button
                            size="sm"
                            variant="outline"
                            className="gap-2"
                            onClick={() => handleDownloadDocument(doc, true)}
                          >
                            <Download className="h-4 w-4" />
                            Download
                          </Button>
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {/* Uploaded Documents Section */}
                {uploadedDocuments.length > 0 && (
                  <div className="mt-4 space-y-3">
                    <div className="flex items-center gap-2 text-sm text-muted-foreground">
                      <File className="h-4 w-4" />
                      <span>Your Uploaded Documents:</span>
                    </div>
                    <div className="border rounded-lg bg-muted/20 p-3">
                      <div className="space-y-2">
                        {uploadedDocuments.map((doc) => (
                          <div key={doc.id} className="flex items-center justify-between p-3 bg-background rounded-lg border">
                            <div className="flex items-center gap-3">
                              <div className="p-2 bg-primary/10 rounded">
                                <FileText className="h-4 w-4 text-primary" />
                              </div>
                              <div>
                                <p className="font-medium text-sm">{doc.file_name}</p>
                                <div className="flex items-center gap-2 text-xs text-muted-foreground">
                                  {doc.file_type && <Badge variant="outline" className="text-xs">{doc.file_type}</Badge>}
                                  <span>{doc.uploaded_at && new Date(doc.uploaded_at).toLocaleDateString()}</span>
                                </div>
                              </div>
                            </div>
                            <div className="flex gap-2">
                              <Button
                                size="sm"
                                variant="ghost"
                                onClick={() => handleViewUploadedDoc(doc.id, doc.file_url)}
                              >
                                <Eye className="h-4 w-4" />
                              </Button>
                              <Button
                                size="sm"
                                variant="ghost"
                                onClick={() => handleDownloadDocument(doc)}
                              >
                                <Download className="h-4 w-4" />
                              </Button>
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>
                  </div>
                )}

                {/* Message Box for all non-draft statuses */}
                {currentFiling.status !== 'DRAFT' && (
                  <div className="mt-4 space-y-3">
                    {/* Conversation Messages */}
                    <div className="border rounded-lg bg-muted/20">
                      <div className="p-3 border-b bg-muted/50">
                        <div className="flex items-center gap-2">
                          <MessageSquare className="h-4 w-4" />
                          <span className="font-medium text-sm">Conversation with Agent</span>
                        </div>
                      </div>
                      <ScrollArea className="h-64 p-3">
                        <div className="space-y-3">
                          {messages.length === 0 && (
                            <p className="text-sm text-muted-foreground text-center py-4">
                              No messages yet. Start a conversation with your agent.
                            </p>
                          )}
                          {messages.map((msg) => (
                            <div
                              key={msg.id}
                              className={cn(
                                "flex",
                                msg.sender_role === 'client' ? 'justify-end' : 'justify-start'
                              )}
                            >
                              <div
                                className={cn(
                                  "max-w-[80%] rounded-lg p-3",
                                  msg.sender_role === 'client'
                                    ? 'bg-primary text-primary-foreground'
                                    : 'bg-muted'
                                )}
                              >
                                <p className="text-xs font-medium mb-1 opacity-70">
                                  {msg.sender_role === 'client' ? 'You' : msg.sender_name || 'Agent'}
                                </p>
                                <p className="text-sm">{msg.message}</p>
                                <p className="text-xs mt-1 opacity-50">
                                  {new Date(msg.created_at).toLocaleTimeString('en-US', {
                                    hour: '2-digit',
                                    minute: '2-digit',
                                  })}
                                </p>
                              </div>
                            </div>
                          ))}
                          <div ref={messagesEndRef} />
                        </div>
                      </ScrollArea>
                      <div className="p-3 border-t">
                        <div className="flex gap-2">
                          <Input
                            placeholder="Type your message..."
                            value={newMessage}
                            onChange={(e) => setNewMessage(e.target.value)}
                            onKeyPress={(e) => e.key === 'Enter' && handleSendMessage()}
                            disabled={sendingMessage}
                          />
                          <Button 
                            onClick={handleSendMessage}
                            disabled={!newMessage.trim() || sendingMessage}
                            className="gap-2"
                          >
                            <Send className="h-4 w-4" />
                            Send
                          </Button>
                        </div>
                      </div>
                    </div>
                  </div>
                )}
                
                {['DRAFT', 'SUBMITTED', 'NEED_INFO'].includes(currentFiling.status) && (
                  <Button 
                    className="mt-4 gap-2"
                    onClick={() => navigate(`/filing/start?id=${currentFiling.id}`)}
                  >
                    {currentFiling.status === 'DRAFT' ? 'Continue Filing' : 'Edit Filing'}
                    <ArrowRight className="h-4 w-4" />
                  </Button>
                )}
              </div>
              
              <Badge className={cn(currentStatusConfig?.badgeClass, "text-white")}>
                {currentFiling.status === 'NEED_INFO' ? 'Action Required' : currentStatusConfig?.label}
              </Badge>
            </div>
          </CardContent>
        </Card>

        {/* Your Tax Professional - Show when agent is assigned */}
        {currentFiling.agent_name && (
          <Card className="card-elevated border-blue-200 dark:border-blue-800 bg-gradient-to-r from-blue-50 to-indigo-50 dark:from-blue-950 dark:to-indigo-950">
            <CardContent className="p-6">
              <div className="flex items-center gap-4">
                <div className="w-14 h-14 bg-blue-600 rounded-full flex items-center justify-center text-white text-lg font-semibold shadow-lg">
                  {currentFiling.agent_name.split(' ').map(n => n[0]).join('').substring(0, 2).toUpperCase()}
                </div>
                <div className="flex-1">
                  <p className="text-sm text-blue-600 dark:text-blue-400 font-medium">Your Tax Professional</p>
                  <p className="text-xl font-bold text-blue-900 dark:text-blue-100">{currentFiling.agent_name}</p>
                  {currentFiling.agent_email && (
                    <p className="text-sm text-muted-foreground">{currentFiling.agent_email}</p>
                  )}
                </div>
                <Button 
                  variant="outline" 
                  className="border-blue-300 text-blue-700 hover:bg-blue-100 dark:border-blue-700 dark:text-blue-300"
                  onClick={() => {
                    const chatSection = document.getElementById('chat-section');
                    if (chatSection) chatSection.scrollIntoView({ behavior: 'smooth' });
                  }}
                >
                  <MessageSquare className="h-4 w-4 mr-2" />
                  Message
                </Button>
              </div>
            </CardContent>
          </Card>
        )}

        {/* Timeline - Only show for non-draft filings */}
        {currentFiling.status !== 'DRAFT' && (
          <Card className="card-elevated">
            <CardHeader>
              <CardTitle>Filing Timeline</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="space-y-8">
                {timelineSteps.map((step, index) => {
                  const Icon = step.icon;
                  const isCompleted = step.status === 'completed';
                  const isCurrent = step.status === 'current';
                  const isPending = step.status === 'pending';
                  
                  return (
                    <div key={step.key} className="relative flex gap-4">
                      {/* Connector line */}
                      {index < timelineSteps.length - 1 && (
                        <div
                          className={cn(
                            'absolute left-4 top-10 h-full w-0.5',
                            isCompleted ? 'bg-green-500' : 'bg-border'
                          )}
                        />
                      )}

                      {/* Icon */}
                      <div
                        className={cn(
                          'relative z-10 flex h-8 w-8 items-center justify-center rounded-full border-2',
                          isCompleted && 'border-green-500 bg-green-500',
                          isCurrent && 'border-yellow-500 bg-yellow-500',
                          isPending && 'border-gray-300 bg-background'
                        )}
                      >
                        <Icon
                          className={cn(
                            'h-4 w-4',
                            (isCompleted || isCurrent) ? 'text-white' : 'text-muted-foreground'
                          )}
                        />
                      </div>

                      {/* Content */}
                      <div className="flex-1 pt-0.5">
                        <div className="flex items-center justify-between">
                          <p
                            className={cn(
                              'font-medium',
                              isPending ? 'text-muted-foreground' : 'text-foreground'
                            )}
                          >
                            {step.label}
                          </p>
                          <span className="text-sm text-muted-foreground">
                            {step.timestamp ? formatDate(step.timestamp) : (isPending ? 'Pending' : '')}
                          </span>
                        </div>
                        
                        {/* Show agent message for NEED_INFO when current - no upload button (use message box in Current Status) */}
                        {step.showMessage && step.message && (
                          <div className="mt-2">
                            <div className="p-3 bg-yellow-50 dark:bg-yellow-900/20 rounded-lg border border-yellow-200 dark:border-yellow-800">
                              <p className="text-sm text-yellow-700 dark:text-yellow-300">
                                {step.message}
                              </p>
                            </div>
                          </div>
                        )}
                        
                        {/* Show generic message for current step without agent message */}
                        {isCurrent && step.key === 'NEED_INFO' && !step.message && (
                          <div className="mt-2">
                            <p className="text-sm text-muted-foreground">
                              Additional information is needed to proceed with your filing. Use the message box above to communicate with your agent.
                            </p>
                          </div>
                        )}
                      </div>
                    </div>
                  );
                })}
              </div>
            </CardContent>
          </Card>
        )}

        {/* Filing Details */}
        <Card className="card-elevated">
          <CardHeader>
            <CardTitle>Filing Details</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-4">
              <div className="flex items-center justify-between">
                <span className="text-muted-foreground">Tax Year</span>
                <span className="font-medium">{currentFiling.tax_year}</span>
              </div>
              <div className="flex items-center justify-between">
                <span className="text-muted-foreground">Filing Type</span>
                <span className="font-medium capitalize">{currentFiling.filing_type}</span>
              </div>
              {currentFiling.estimated_refund && (
                <div className="flex items-center justify-between">
                  <span className="text-muted-foreground">Estimated Refund</span>
                  <span className="font-medium text-green-600">
                    ${currentFiling.estimated_refund.toLocaleString()}
                  </span>
                </div>
              )}
              {currentFiling.balance_due && (
                <div className="flex items-center justify-between">
                  <span className="text-muted-foreground">Balance Due</span>
                  <span className="font-medium text-red-600">
                    ${currentFiling.balance_due.toLocaleString()}
                  </span>
                </div>
              )}
              <div className="flex items-center justify-between">
                <span className="text-muted-foreground">Created</span>
                <span className="font-medium">{formatDate(currentFiling.created_at)}</span>
              </div>
              {currentFiling.submitted_at && (
                <div className="flex items-center justify-between">
                  <span className="text-muted-foreground">Submitted</span>
                  <span className="font-medium">{formatDate(currentFiling.submitted_at)}</span>
                </div>
              )}
              {currentFiling.submitted_to_irs_at && (
                <div className="flex items-center justify-between">
                  <span className="text-muted-foreground">Submitted to IRS</span>
                  <span className="font-medium">{formatDate(currentFiling.submitted_to_irs_at)}</span>
                </div>
              )}
            </div>
          </CardContent>
        </Card>

        {/* Multiple Filings - Show other filings if available */}
        {filings.length > 1 && (
          <Card className="card-elevated">
            <CardHeader>
              <CardTitle>Other Filings</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="space-y-3">
                {filings
                  .filter(f => f.id !== currentFiling.id)
                  .slice(0, 5)
                  .map((filing) => {
                    const config = STATUS_CONFIG[filing.status] || STATUS_CONFIG.DRAFT;
                    return (
                      <div
                        key={filing.id}
                        className="flex items-center justify-between p-3 rounded-lg border hover:bg-muted/50 cursor-pointer"
                        onClick={() => setCurrentFiling(filing)}
                      >
                        <div>
                          <p className="font-medium">Tax Year {filing.tax_year}</p>
                          <p className="text-sm text-muted-foreground capitalize">{filing.filing_type}</p>
                        </div>
                        <Badge className={cn(config.badgeClass, "text-white")}>
                          {config.label}
                        </Badge>
                      </div>
                    );
                  })}
              </div>
            </CardContent>
          </Card>
        )}
      </div>
    </DashboardLayout>
  );
}
